<script setup lang="ts">
import { computed, useSlots } from 'vue';

import ButtonIcon from '@/shared/components/UI/Buttons/ButtonIcon.vue';
import IconX from '@/shared/components/UI/Icons/IconX.vue';
import { useModal, UseModalEmitType } from '@/shared/composables/modal';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const slots = useSlots();

const emit = defineEmits<UseModalEmitType>();

const props = withDefaults(
  defineProps<{
    active: boolean;
    size?: ModalSize;
    closeButton?: boolean;
    closeLabel?: string;
    ariaLabel?: string;
    closeOnEscapeKey?: boolean;
    closeOnClickOutside?: boolean;
    alwaysOnTop?: boolean;
    zIndex?: string;
    footerDivider?: boolean;
    padded?: boolean;
  }>(),
  {
    size: 'md',
    closeButton: true,
    closeLabel: 'Close',
    ariaLabel: undefined,
    closeOnEscapeKey: true,
    closeOnClickOutside: true,
    alwaysOnTop: false,
    zIndex: undefined,
    footerDivider: true,
    padded: false
  }
);

const {
  modalClickOutsideRef: _modalClickOutsideRef,
  modalScrollRef: _modalScrollRef,
  close
} = useModal(props, emit);

const modalSizeClasses = new Map<ModalSize, string>([
  ['sm', 'max-w-sm'],
  ['md', 'max-w-[524px]'],
  ['lg', 'max-w-2xl'],
  ['xl', 'max-w-4xl'],
  ['2xl', 'max-w-5xl']
]);

const modalZindex = computed(() => {
  if (props.zIndex) {
    return props.zIndex;
  }
  return props.alwaysOnTop ? 'z-30' : 'z-modal';
});

const classes = computed(() => modalSizeClasses.get(props.size));
</script>

<template>
  <!-- Backdrop -->
  <Teleport to="body">
    <Transition
      enter-from-class="opacity-0"
      enter-active-class="transition-opacity duration-300 ease-in-out"
      leave-active-class="transition-opacity duration-300 ease-in-out"
      leave-to-class="opacity-0"
    >
      <div
        v-if="active"
        aria-hidden="true"
        class="fixed left-0 top-0 h-lvh w-full bg-black/30"
        :class="modalZindex"
      ></div>
    </Transition>

    <Transition
      enter-from-class="translate-y-20 opacity-0"
      enter-active-class="transition-all duration-300 ease-in-out"
      leave-active-class="transition-all duration-300 ease-in-out"
      leave-to-class="translate-y-20 opacity-0"
    >
      <div
        v-if="active"
        class="fixed left-0 top-0 flex h-lvh w-full items-center justify-center"
        :class="modalZindex"
      >
        <div
          ref="_modalClickOutsideRef"
          :aria-label="ariaLabel"
          aria-modal="true"
          role="dialog"
          :class="classes"
          class="relative mx-2 max-h-[90dvh] w-full overflow-hidden rounded-2xl bg-white shadow-xl md:mx-8"
        >
          <!-- eslint-disable tailwindcss/no-custom-classname -->
          <ButtonIcon
            v-if="closeButton"
            theme="light"
            :label="closeLabel"
            :aria-label="closeLabel"
            class="!text-headings-dark absolute right-4 top-4 z-10"
            @click="close"
            ><IconX
          /></ButtonIcon>
          <!-- eslint-enable tailwindcss/no-custom-classname -->

          <div
            ref="_modalScrollRef"
            class="flex h-full max-h-[90dvh] flex-col gap-6 overflow-y-auto p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            :class="padded ? 'md:p-10' : 'md:p-6'"
          >
            <div
              v-if="slots.header"
              class="flex items-center justify-between gap-2"
            >
              <slot name="header" />
            </div>

            <div v-if="slots.body">
              <slot name="body" />
            </div>
            <template v-if="slots.default">
              <slot name="default" />
            </template>
            <div
              v-if="slots.footer"
              :class="{ 'border-t pt-6': footerDivider }"
            >
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
